/** The three detail tabs below the two grids. */
export type SubPanelTabKey = 'comparison' | 'historical' | 'detail'

export interface SubPanelTab {
  key: SubPanelTabKey
  label: string
  /**
   * Render the tab greyed out and refuse to activate it (click and keyboard). The tab stays in the
   * strip so the layout matches the design; re-enabling it is a matter of dropping this flag.
   */
  disabled?: boolean
  /** Tooltip shown on hover over a disabled tab, explaining why it cannot be opened. */
  disabledReason?: string
}

export const SUB_PANEL_TABS: SubPanelTab[] = [
  { key: 'comparison', label: 'Position Comparison' },
  {
    key: 'historical',
    label: 'Historical Balance',
    // Not built yet — kept visible but unselectable until it is.
    disabled: true,
    disabledReason: 'Historical balance not yet supported',
  },
  { key: 'detail', label: 'Position Detail' },
]

export const DEFAULT_SUB_PANEL_TAB: SubPanelTabKey = 'comparison'

/** Whether `key` names a tab that is currently disabled. */
export function isSubPanelTabDisabled(key: SubPanelTabKey): boolean {
  return SUB_PANEL_TABS.some(tab => tab.key === key && tab.disabled === true)
}

/**
 * The tab to actually show for a requested (typically persisted) key. A disabled or unknown key
 * falls back to {@link DEFAULT_SUB_PANEL_TAB}. The persisted value itself is left alone, so a layout
 * saved on a tab that is later re-enabled reopens on it.
 */
export function resolveSubPanelTab(
  key: SubPanelTabKey | null | undefined,
): SubPanelTabKey {
  if (key == null) return DEFAULT_SUB_PANEL_TAB
  const known = SUB_PANEL_TABS.some(tab => tab.key === key)
  return known && !isSubPanelTabDisabled(key) ? key : DEFAULT_SUB_PANEL_TAB
}

/** Header + body for the shared `hs-panel-card` the sub-panels show in place of content. */
export interface SubPanelEmptyState {
  header: string
  body: string
}

/**
 * Nothing selected at all.
 *
 * Position Comparison shows this as its ONLY empty state: it resolves its anchor through
 * `WorkbenchStoreService.anchorRow()`, which has no cardinality gate — a multi-row selection simply
 * anchors on the most recently selected row, exactly as Positions 1.0 does. Sub-panels that instead
 * require *exactly one* selected row (Position Detail, via
 * `WorkbenchStoreService.anchorSelection()`) pair this with
 * {@link subPanelMultipleSelectedState} for the >1 case.
 */
export function subPanelEmptyState(): SubPanelEmptyState {
  return {
    header: 'No Position Selected',
    body:
      'This panel requires a position to be selected before information can be displayed. ' +
      'Select a position to continue.',
  }
}

/**
 * More than one row is selected, in a sub-panel that can only show one.
 *
 * Copy is taken verbatim from the Positions 1.0 External Position Detail card
 * (`libs/panels/positions/src/lib/external-position-detail/external-position-detail-panel.component.html`),
 * which this sub-panel replaces — the two panels have to read alike for a user moving between them.
 */
export function subPanelMultipleSelectedState(): SubPanelEmptyState {
  return {
    header: 'Multiple Positions Selected',
    body:
      'This panel requires a single position row to be selected in order for the data to be ' +
      'loaded.',
  }
}
